import { Box, Divider, Grid, useTheme } from "@mui/material";
import type { PropertyBrochureData } from "./propertyBrochure.types";
import MxImageBlur from "@rf-l1/images/MxImageBlur";
import MxTypography from "@rf-l1/MxTypography";
import { IconMapPinFilled } from "@tabler/icons-react";
import MxIcons from "@rf-l1/MxIcons";
import Agua from "@icons-rf/properties-item/agua.svg?react";
import Bath from "@icons-rf/properties-item/bath.svg?react";
import Bed from "@icons-rf/properties-item/bed.svg?react";
import FloorsTotal from "@icons-rf/properties-item/floors-total.svg?react";
import Carro from "@icons-rf/properties-item/carro.svg?react";
import Casa2 from "@icons-rf/properties-item/casa2.svg?react";
import HalfBath from "@icons-rf/properties-item/half-bath.svg?react";
import Measure from "@icons-rf/properties-item/medida.svg?react";
import Pump from "@icons-rf/properties-item/pumps.svg?react";
import useUserHelp from "@rf-hooks/useUserHelp";
import { useMemo } from "react";
import { renderToStaticMarkup } from "react-dom/server";
import QRCode from "react-qr-code";
import { formatPhoneNumber } from "@/src-refactor/modules/shared/helpers/format-phone-number.helper";
import ENVIRONMENT_VAR from "@/src-refactor/modules/shared/model/const/env/env.const";

interface PropertyBrochureTemplateProps {
  data: PropertyBrochureData;
}

const IMAGE_PROXY_PATH = "/v2/download-image?url=";

const toPdfSafeImageUrl = (url?: string): string => {
  if (!url) {
    return "";
  }

  if (url.startsWith("data:") || url.startsWith("blob:")) {
    return url;
  }

  if (url.includes(IMAGE_PROXY_PATH)) {
    return url;
  }

  if (!/^https?:\/\//i.test(url) || !ENVIRONMENT_VAR.API_URL) {
    return url;
  }

  return `${ENVIRONMENT_VAR.API_URL}${IMAGE_PROXY_PATH}${encodeURIComponent(url)}`;
};

const buildBrochureUrlWithReferer = (
  projectUrl: string,
  peopleId?: number | null
): string => {
  if (!projectUrl) {
    return "";
  }

  if (!Number.isInteger(peopleId) || !peopleId) {
    return projectUrl;
  }

  try {
    const brochureUrl = new URL(projectUrl);
    brochureUrl.searchParams.set("referer", peopleId.toString());
    return brochureUrl.toString();
  } catch {
    return projectUrl;
  }
};

const PropertyBrochureTemplate = ({ data }: PropertyBrochureTemplateProps) => {
  const { palette } = useTheme();
  const { USER } = useUserHelp();
  const userData = USER?.userData;

  const brochureImages = useMemo(
    () => data.images.map(imageUrl => toPdfSafeImageUrl(imageUrl)),
    [data.images]
  );

  const brochureAgents = useMemo(() => {
    if (userData) {
      return [
        {
          id: userData.user_id ?? userData.people_id ?? 0,
          name: userData.name ?? "",
          phone: formatPhoneNumber(userData.mobile ?? userData.phone ?? ""),
          email: userData.email ?? "",
          picture: toPdfSafeImageUrl(userData.picture ?? "")
        }
      ];
    }

    return data.agents.map(agent => ({
      ...agent,
      picture: toPdfSafeImageUrl(agent.picture ?? "")
    }));
  }, [data.agents, userData]);

  const brochureUrl = useMemo(
    () => buildBrochureUrlWithReferer(data.projectUrl, userData?.people_id),
    [data.projectUrl, userData?.people_id]
  );

  return (
    <div id="property-brochure">
      <style>{`
        @page { size: A4; margin: 0; }
        body {
          margin: 0;
          font-family: "Helvetica Neue", Arial, sans-serif;
          background: #f3f4f7;
          }
        #property-brochure {
          width: 210mm;
          min-height: 295mm;
          background: #fff;
          color: #1f2933;
          box-sizing: border-box;
        }
        
      `}</style>

      <Grid container>
        <Grid item xs={12} position="relative">
          <Gallery images={brochureImages} />
          <Box
            sx={{
              position: "absolute",
              zIndex: 10,
              top: 0,
              background: palette.primary.main,
              padding: "0.5rem 1rem",
              color: "white",
              borderBottomRightRadius: 10,
              fontSize: "13px"
            }}
          >
            #{data.id}
          </Box>
        </Grid>
        <Grid container item xs={8} py={3} px={4}>
          <Grid container item xs={12} rowGap={1.5}>
            <Grid item xs={12}>
              <TitleProperty title={data.title} />
            </Grid>
            <Grid item xs={12}>
              <NameProperty name={data.nameProperty} />
            </Grid>
            <Grid item xs={12}>
              <LocationProperty city={data.city} sector={data.sector} />
            </Grid>
            <Grid item xs={12} my={1.5}>
              <PriceProperty price={data.prices} />
            </Grid>
            <Grid item xs={12}>
              <Divider
                sx={{
                  borderColor: "#f7f5ee"
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <Description description={data.description} />
            </Grid>
            <Grid item xs={12}>
              <Divider
                sx={{
                  borderColor: "#f7f5ee"
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <Characteristics characteristics={data.characteristics} />
            </Grid>
            <Grid item xs={12}>
              <Divider
                sx={{
                  borderColor: "#f7f5ee"
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <GeneralInfo
                uses={data.uses}
                conditionStatus={data.conditionStatus}
                position={data.position}
                visitInfo={data.visit}
              />
            </Grid>
            <Grid item xs={12}>
              <Divider
                sx={{
                  borderColor: "#f7f5ee"
                }}
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid container item xs={4} pt={8} pr={4} alignSelf={"flex-start"}>
          <Grid item xs={12} mb={5}>
            <Agents agents={brochureAgents} />
          </Grid>
          <Grid item xs={12}>
            <Feature feature={data.feature} />
          </Grid>
        </Grid>
        {Boolean(brochureUrl) && (
          <Grid
            container
            item
            xs={12}
            justifyContent="flex-end"
            alignItems="center"
            pr={4}
            columnSpacing={2}
            sx={{ position: "absolute", bottom: 20, right: 0 }}
          >
            <Grid item xs={2.5}>
              <MxTypography color="primary.main" variant="overline" fontWeight={500}>
                Escanea para ver más detalles en línea o cliquea el QR
              </MxTypography>
            </Grid>
            <Grid item xs="auto">
              <BrochureQrCode url={brochureUrl} />
            </Grid>
          </Grid>
        )}
      </Grid>
    </div>
  );
};

export default PropertyBrochureTemplate;

const Gallery = (props: {
  images: PropertyBrochureTemplateProps["data"]["images"];
}) => {
  return (
    <Grid container spacing={1}>
      <Grid item xs={8}>
        <MxImageBlur
          src={props.images[0]}
          width={"100%"}
          height={350}
          objectFit="cover"
        />
      </Grid>

      <Grid container item xs={4} spacing={1}>
        {props.images.slice(1, 3).map(x => (
          <Grid item xs={12}>
            <MxImageBlur src={x} width={"100%"} height={341 / 2} objectFit="cover" />
          </Grid>
        ))}
      </Grid>
    </Grid>
  );
};
const TitleProperty = (props: {
  title: PropertyBrochureTemplateProps["data"]["title"];
}) => {
  return (
    <MxTypography variant="h4" color="primary.main" fontWeight={800}>
      {props.title?.toUpperCase()}
    </MxTypography>
  );
};
const NameProperty = (props: {
  name: PropertyBrochureTemplateProps["data"]["nameProperty"];
}) => {
  return (
    <MxTypography variant="overline" color="grey.700" fontWeight={300}>
      {props.name?.toUpperCase()}
    </MxTypography>
  );
};
const LocationProperty = (props: {
  sector: PropertyBrochureTemplateProps["data"]["sector"];
  city: PropertyBrochureTemplateProps["data"]["city"];
}) => {
  return (
    <Grid container alignItems="center" columnSpacing={0.5}>
      <Grid item xs="auto" marginTop={-0.5}>
        <MxIcons color={"error.main"} size={16}>
          <IconMapPinFilled />
        </MxIcons>
      </Grid>
      <Grid item xs="auto">
        <MxTypography variant="overline" color="grey.700" fontWeight={300}>
          {props.sector?.toUpperCase()},
        </MxTypography>
      </Grid>
      <Grid item xs="auto">
        <MxTypography variant="overline" color="grey.700" fontWeight={300}>
          {props.city?.toUpperCase()}
        </MxTypography>
      </Grid>
    </Grid>
  );
};
const PriceProperty = (props: {
  price: PropertyBrochureTemplateProps["data"]["prices"];
}) => {
  const size = props.price.length > 1 ? 5 : 11;
  return (
    <Grid container alignItems="center" columnSpacing={0.5}>
      {props.price.map(x => (
        <Grid container item xs={size} rowSpacing={1}>
          <Grid item xs={12}>
            <MxTypography variant="overline" color="grey.700" fontWeight={500}>
              {x.label}
            </MxTypography>
          </Grid>
          <Grid item xs={12}>
            <MxTypography variant="overline" color="error.main" fontWeight={600}>
              {x.value}
            </MxTypography>
          </Grid>
        </Grid>
      ))}
    </Grid>
  );
};
const Description = (props: {
  description: PropertyBrochureTemplateProps["data"]["description"];
}) => {
  return (
    <Grid container alignItems="center" rowGap={2}>
      <Grid item xs={12}>
        <MxTypography variant="subtitle2" color="black" fontWeight={600}>
          Descripción
        </MxTypography>
      </Grid>
      <Grid item xs={12}>
        <MxTypography variant="overline" color="black" fontWeight={300}>
          {props.description.substring(0, 800)}...
        </MxTypography>
      </Grid>
    </Grid>
  );
};
const Characteristics = (props: {
  characteristics: PropertyBrochureTemplateProps["data"]["characteristics"];
}) => {
  return (
    <Grid container alignItems="center" rowGap={2}>
      <Grid item xs={12}>
        <MxTypography variant="subtitle2" color="black" fontWeight={600}>
          Características
        </MxTypography>
      </Grid>
      <Grid container item xs={12} spacing={1} rowSpacing={2.5}>
        {props.characteristics.map(x => (
          <Grid container item xs={3} rowSpacing={0.5}>
            <Grid item xs={12}>
              <MxTypography variant="overline" color="primary.main" fontWeight={600}>
                {x.extraData?.type}:
              </MxTypography>
            </Grid>
            <Grid item xs={12}>
              <MxTypography variant="overline" color="black" fontWeight={300}>
                {x.name}
              </MxTypography>
            </Grid>
          </Grid>
        ))}
      </Grid>
    </Grid>
  );
};
const GeneralInfo = (props: {
  uses: PropertyBrochureTemplateProps["data"]["uses"];
  conditionStatus: PropertyBrochureTemplateProps["data"]["conditionStatus"];
  position: PropertyBrochureTemplateProps["data"]["position"];
  visitInfo: PropertyBrochureTemplateProps["data"]["visit"];
}) => {
  return (
    <Grid container alignItems="center" rowGap={2}>
      <Grid item xs={12}>
        <MxTypography variant="subtitle2" color="black" fontWeight={600}>
          Información General
        </MxTypography>
      </Grid>
      <Grid container item xs={12} spacing={1} rowSpacing={2.5}>
        {props.conditionStatus && (
          <Grid container item xs={6} spacing={0.5}>
            <Grid item xs="auto">
              <MxTypography variant="overline" color="primary.main" fontWeight={500}>
                Estado del inmueble:
              </MxTypography>
            </Grid>
            <Grid item xs="auto">
              <MxTypography variant="overline" color="black" fontWeight={300}>
                {props.conditionStatus}
              </MxTypography>
            </Grid>
          </Grid>
        )}

        {props.position && (
          <Grid container item xs={6} spacing={0.5}>
            <Grid item xs="auto">
              <MxTypography variant="overline" color="primary.main" fontWeight={500}>
                Posición:
              </MxTypography>
            </Grid>
            <Grid item xs="auto">
              <MxTypography variant="overline" color="black" fontWeight={300}>
                {props.position}
              </MxTypography>
            </Grid>
          </Grid>
        )}
        {props.uses && (
          <Grid container item xs={6} spacing={0.5}>
            <Grid item xs="auto">
              <MxTypography variant="overline" color="primary.main" fontWeight={500}>
                Uso del inmueble:
              </MxTypography>
            </Grid>
            <Grid item xs="auto">
              <MxTypography variant="overline" color="black" fontWeight={300}>
                {props.uses}
              </MxTypography>
            </Grid>
          </Grid>
        )}
        {props.visitInfo && (
          <Grid container item xs={6} spacing={0.5}>
            <Grid item xs="auto">
              <MxTypography variant="overline" color="primary.main" fontWeight={500}>
                Información de visita:
              </MxTypography>
            </Grid>
            <Grid item xs="auto">
              <MxTypography variant="overline" color="black" fontWeight={300}>
                {props.visitInfo}
              </MxTypography>
            </Grid>
          </Grid>
        )}
      </Grid>
    </Grid>
  );
};
const Agents = (props: {
  agents: PropertyBrochureTemplateProps["data"]["agents"];
}) => {
  if (!props.agents?.length) {
    return null;
  }

  return (
    <Grid container alignItems="center" rowGap={2}>
      <Grid item xs={12} marginLeft={8}>
        <MxTypography variant="subtitle2" color="black" fontWeight={600}>
          Agente Inmobiliario
        </MxTypography>
      </Grid>
      {props.agents.map((x, index) => (
        <Grid
          container
          item
          xs={12}
          columnSpacing={2}
          key={(x.id ?? x.email ?? x.name ?? "agent") + index.toString()}
        >
          <Grid item xs={3}>
            <MxImageBlur
              width={50}
              height={50}
              borderRadius={100}
              objectFit="cover"
              src={x.picture}
              alt={x.name}
            />
          </Grid>
          <Grid container item xs={9} spacing={0.5}>
            <Grid item xs={12}>
              <MxTypography
                variant="caption"
                color="black"
                fontWeight={600}
                lines={1}
              >
                {x.name}
              </MxTypography>
            </Grid>
            <Grid item xs={12}>
              <MxTypography variant="overline" color="grey.700" fontWeight={400}>
                {x.phone}
              </MxTypography>
            </Grid>
            <Grid item xs={12}>
              <MxTypography variant="overline" color="grey.700" fontWeight={400}>
                {x.email}
              </MxTypography>
            </Grid>
          </Grid>
        </Grid>
      ))}
    </Grid>
  );
};
const Feature = (props: {
  feature: PropertyBrochureTemplateProps["data"]["feature"];
}) => {
  if (props.feature.length == 0) return null;
  return (
    <Grid container rowGap={2} marginLeft={8}>
      <Grid item xs={12}>
        <MxTypography variant="subtitle2" color="black" fontWeight={600}>
          Distribución
        </MxTypography>
      </Grid>
      {props.feature.map(x => (
        <Grid
          container
          item
          xs={7.5}
          spacing={0.5}
          columnSpacing={1.5}
          alignItems="center"
        >
          <Grid item xs="auto">
            <MxIcons color="grey.700">
              {x.icon == "bed" && <Bed />}
              {x.icon == "bath" && <Bath />}
              {x.icon == "half-bath" && <HalfBath />}
              {x.icon == "parking" && <Carro />}
              {x.icon == "level" && <Agua />}
              {x.icon == "floors" && <FloorsTotal />}
              {x.icon == "construction" && <Casa2 />}
              {x.icon == "land" && <Measure />}
              {x.icon == "pump" && <Pump />}
            </MxIcons>
          </Grid>
          <Grid item xs="auto">
            <MxTypography variant="overline" color="grey.700" fontWeight={400}>
              {x.value}
            </MxTypography>
          </Grid>
          <Grid item xs="auto">
            <MxTypography variant="overline" color="grey.700" fontWeight={400}>
              {x.label}
            </MxTypography>
          </Grid>
        </Grid>
      ))}
    </Grid>
  );
};

const BrochureQrCode = ({ url, size = 144 }: { url: string; size?: number }) => {
  const qrImageSrc = useMemo(() => {
    if (!url) {
      return null;
    }

    try {
      const svgMarkup = renderToStaticMarkup(
        <QRCode value={url} size={size} level="H" />
      );

      if (typeof window === "undefined") {
        return null;
      }

      const encodedSvg = window.btoa(unescape(encodeURIComponent(svgMarkup)));
      return `data:image/svg+xml;base64,${encodedSvg}`;
    } catch (error) {
      console.error("No se pudo generar el código QR del brochure.", error);
      return null;
    }
  }, [url]);

  if (!qrImageSrc) {
    return null;
  }

  return (
    <a href={url} target="_blank" rel="noopener noreferrer">
      <img
        src={qrImageSrc}
        width={80}
        height={80}
        alt="Código QR del proyecto"
        style={{ display: "block" }}
      />
    </a>
  );
};
